import { forwardRef, useCallback, useEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { ButtonClose } from '@hivebrite/design-system/Button';
import { IconPlus } from '@hivebrite/design-system/Icon';
import { Loader } from '@hivebrite/design-system/Loader';
import { Simplebar } from '@hivebrite/design-system/Simplebar';
import { focusOutline } from '@hivebrite/design-system/Theme';
import { usePrevious } from '@hivebrite/react-hooks';

import InputFileHidden from 'hb-react/shared/components/DesignSystem/QuillEditor/Toolbar/components/InputFileHidden';
import { isInLoadingState } from 'hb-react/shared/hooks/useGlobalStorageV2/utils';
import useVirusScanningEnabled from 'hb-react/shared/hooks/useVirusScanningEnabled';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';
import onEnterPress from 'hb-react/shared/utils/onEnterPress';

import { useQuillEditorContext } from '../contexts/QuillEditorContext';
import MediaUploadError from '../MediaUploadError/index';
import { ACCEPT_IMAGES } from '../Toolbar';

const Image = ({ image, onRemoveImage, error }) => {
  const virusScanningEnabled = useVirusScanningEnabled();
  const [preview, setPreview] = useState(
    (image.url || image.download_url) ?? null,
  );

  // HACK: While we support QuilEditor V1 and V2/3, ignore the virus scan status
  // if attachment is not a GlobalStorageFile (i.e. has a loading property OR no key property)
  const keys = Object.keys(image);
  const isLoading =
    !preview ||
    (keys.includes('loading') || !keys.includes('key')
      ? image.loading
      : isInLoadingState(image, virusScanningEnabled));

  const handleRemoveImage = useCallback(
    () => onRemoveImage(image),
    [onRemoveImage, image],
  );

  useEffect(() => {
    if (preview === null && image.file) {
      const reader = new FileReader();

      reader.readAsDataURL(image.file);

      reader.onloadend = () => setPreview(reader.result);
    }
  }, [image, preview]);

  return (
    <WrapperImage data-testid="quill-preview-image">
      <ImagePreviewWrapper $error={Boolean(error)}>
        {!isLoading ? (
          <>
            <ImagePreview $preview={preview} data-testid="image-preview" />
            {error && <MediaUploadError error={error} />}
          </>
        ) : (
          <Loader size={72} />
        )}
      </ImagePreviewWrapper>
      {!isLoading && (
        <Box
          css={css`
            position: absolute;
            right: 10px;
            top: 10px;
          `}
        >
          <ButtonClose
            aria-label={__('Remove')}
            variant="inversed"
            onClick={handleRemoveImage}
          />
        </Box>
      )}
    </WrapperImage>
  );
};

const Images = forwardRef(
  ({ singleImageUpload = false, type = 'image', errorsOnly }, ref) => {
    const {
      onAddFiles,
      onRemoveFile,
      images: allImages,
    } = useQuillEditorContext();

    const images = allImages.filter(
      (image) => (!errorsOnly || image.error) && !image.isRemoved,
    );

    const prevImages = usePrevious(images);

    const simpleBarRef = useRef();
    const inputTypeImageRef = useRef();

    const handleAddImages = useCallback(
      (files) => onAddFiles({ type, files }),
      [onAddFiles, type],
    );
    const handleClickImageAdd = useCallback(() => {
      inputTypeImageRef.current.value = null;
      inputTypeImageRef.current.click();
    }, []);
    const handleScrollRight = useCallback(() => {
      if (simpleBarRef.current) {
        const scrollElement = simpleBarRef.current.getScrollElement();

        if (scrollElement) {
          scrollElement.scrollLeft = scrollElement.scrollWidth;
        }
      }
    }, []);

    useEffect(() => {
      if (images.length > 0) {
        // Force recalculate scrollbar
        try {
          if (simpleBarRef.current && simpleBarRef.current.recalculate) {
            simpleBarRef.current.recalculate();
          }
        } catch (error) {
          // Only capture exception if it's not a simple "element not found" error
          if (error.message && !error.message.includes('offsetWidth')) {
            errorReporting.captureException(error);
          }
        }
      }

      // If new image(s) scroll to the end
      if (prevImages && images.length > prevImages.length) {
        handleScrollRight();
      }

      // First upload scroll to the end
      if (!prevImages) {
        handleScrollRight();
      }
    }, [prevImages, images, handleScrollRight]);

    if (!images?.length) {
      return null;
    }

    return (
      <WrapperScroll ref={ref}>
        <Simplebar ref={simpleBarRef} autoHide={false}>
          <WrapperImages>
            {images.map((image) => (
              <Image
                key={image.key || image.id}
                image={image}
                onRemoveImage={onRemoveFile}
                error={image.error}
              />
            ))}
            {!singleImageUpload && !errorsOnly && (
              <Box>
                <WrapperImageAdd
                  onClick={handleClickImageAdd}
                  onKeyPress={onEnterPress(handleClickImageAdd)}
                >
                  <IconPlus size={80} />
                </WrapperImageAdd>
              </Box>
            )}
          </WrapperImages>
        </Simplebar>

        <InputFileHidden
          ref={inputTypeImageRef}
          accept={ACCEPT_IMAGES}
          onChange={handleAddImages}
        />
      </WrapperScroll>
    );
  },
);

export default Images;

const WrapperScroll = styled(Box).attrs(() => ({
  display: 'block',
  px: 3,
}))`
  .simplebar-track.simplebar-horizontal {
    bottom: 0;
    height: ${(props) => props.theme.space[6]}px;

    .simplebar-scrollbar {
      height: 100%;
      top: 0;

      &:before {
        height: 4px;
        top: 9px;
      }
    }
  }
`;
const WrapperImages = styled(Box).attrs(() => ({
  mb: 6,
  pt: 1,
}))`
  > *:first-child {
    padding-left: 2px;
  }

  > *:last-child {
    padding-right: 2px;
  }

  > * + * {
    margin-left: ${(props) => props.theme.space[4]}px;
  }
`;
const WrapperImage = styled(Box).attrs(() => ({
  height: 150,
  minWidth: 150,
}))`
  position: relative;
`;
const WrapperImageAdd = styled(WrapperImage).attrs(() => ({
  alignItems: 'center',
  justifyContent: 'center',
  color: 'primary',
  tabIndex: 0,
}))`
  ${focusOutline}

  border: 1px solid ${(props) => props.theme.colors.horizon};
  border-radius: ${({ theme }) => theme.themeVariables.globalBorderRadius}px;
  cursor: pointer;

  &:focus,
  &:hover {
    color: ${(props) => props.theme.colors.primaryDark};
  }
`;

const ImagePreviewWrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  bg: 'smoke',
  color: 'dove',
  justifyContent: 'center',
  width: '100%',
}))`
  border-radius: ${({ theme }) => theme.themeVariables.globalBorderRadius}px;
  outline: 1px solid
    ${(props) => (props.$error ? props.theme.colors.fire : 'transparent')};
`;

const ImagePreview = styled(Box).attrs(() => ({
  height: '100%',
  width: '100%',
}))`
  background-image: ${(props) => `url(${props.$preview})`};
  border-radius: ${({ theme }) => theme.themeVariables.globalBorderRadius}px;
  background-size: cover;
`;
